// Turning a teacher's correction into something a learner can read at a glance.
//
// The page used to show the answer three times over: what you wrote, what the
// teacher wrote, and a character-level diff of the two. The diff was the only
// part that carried the information, and it was the least readable of the
// three — `diffChars` on Chinese finds accidental matches inside unrelated
// words, so a rewritten clause came back as a candy-stripe of one-character
// insertions and deletions ("穿过降在大雪里", half red, half green).
//
// The backend already segments both texts into words with pinyin
// (`answerContect` / `correctedAnswerContect`), so we diff those tokens
// instead. A word is the unit the teacher actually changed, which makes the
// runs long enough to read, and every token keeps its pinyin and its
// dictionary entry so the marked-up text stays as tappable as the story.

export interface CorrectionToken {
  word: string
  pinyin?: string
  english?: string
}

export type ChangeKind = 'same' | 'added' | 'removed'

/** A stretch of tokens the teacher left alone, added, or struck out. */
export interface CorrectionRun {
  kind: ChangeKind
  tokens: CorrectionToken[]
}

export interface CorrectionMarkup {
  runs: CorrectionRun[]
  /**
   * Free-text remarks the teacher wrote in 【brackets】 at the end of the
   * corrected answer. Pulled out of the text: inline they read as one enormous
   * insertion in the middle of the learner's own sentence.
   */
  notes: string[]
  /** Edits, counted the way a learner would: one swap is one change. */
  changeCount: number
}

const isBlank = (token: CorrectionToken | undefined): boolean => !token?.word || !token.word.trim()

/**
 * The segmentation fields are only *usually* arrays.
 *
 * `getClosedCorrection` segments each side of the correction, but when the text
 * is falsy it echoes the raw value back instead: a correction the teacher closed
 * without writing anything arrives as `correctedAnswerContect: ''`. An empty
 * string has no `forEach`, so building the markup threw and — with no error
 * boundary above it — took the whole corrections page down to a blank screen for
 * every learner with one such row in their list. Coerce anything that is not an
 * array to no tokens: this function is fed straight from the network and must
 * never be the thing that decides whether the page renders at all.
 */
const asTokens = (value: unknown): CorrectionToken[] =>
  Array.isArray(value) ? (value as CorrectionToken[]).filter((token) => token && typeof token === 'object') : []

/** Which side of the correction each view keeps. */
export type CorrectionView = 'markup' | 'written' | 'corrected'

export const runIsVisible = (kind: ChangeKind, view: CorrectionView): boolean => {
  if (view === 'written') return kind !== 'added'
  if (view === 'corrected') return kind !== 'removed'
  return true
}

/**
 * Splits the teacher's bracketed remarks off the end of the corrected tokens.
 *
 * An unclosed bracket is left in the body rather than swallowed: dropping text
 * we could not parse would quietly delete part of the correction the learner
 * paid for.
 */
export const splitTeacherNotes = (input: unknown): { body: CorrectionToken[]; notes: string[] } => {
  const body: CorrectionToken[] = []
  const notes: string[] = []
  let open: string | null = null

  // Character-wise, because a bracket can share a token with real text
  // ("好。【看这里】再见" comes back as one token from the segmenter).
  asTokens(input).forEach((token) => {
    const word = token?.word ?? ''
    let plain = ''

    const flush = () => {
      if (plain) body.push({ ...token, word: plain })
      plain = ''
    }

    Array.from(word).forEach((char) => {
      if (open === null) {
        if (char === '【') {
          flush()
          open = ''
        } else {
          plain += char
        }
        return
      }
      if (char === '】') {
        const text = open.trim()
        if (text) notes.push(text)
        open = null
        return
      }
      open += char
    })

    flush()
  })

  // Never closed: put it back, bracket and all. Dropping text we could not
  // parse would quietly delete part of the correction.
  if (open !== null) body.push({ word: `【${open}` })

  return { body, notes }
}

/**
 * Longest common subsequence over the token words. Answers run to a couple of
 * hundred tokens, so the plain O(n·m) table is comfortably fast and, unlike a
 * heuristic, never invents a match.
 */
const commonSubsequence = (before: CorrectionToken[], after: CorrectionToken[]): boolean[][] => {
  const table: number[][] = Array.from({ length: before.length + 1 }, () => new Array(after.length + 1).fill(0))

  for (let i = before.length - 1; i >= 0; i--) {
    for (let j = after.length - 1; j >= 0; j--) {
      table[i][j] =
        before[i].word === after[j].word ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1])
    }
  }

  // Walk the table back into a per-token "is this part of the match" map.
  const keptBefore = new Array(before.length).fill(false)
  const keptAfter = new Array(after.length).fill(false)
  let i = 0
  let j = 0
  while (i < before.length && j < after.length) {
    if (before[i].word === after[j].word) {
      keptBefore[i] = true
      keptAfter[j] = true
      i++
      j++
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      i++
    } else {
      j++
    }
  }

  return [keptBefore, keptAfter]
}

const pushRun = (runs: CorrectionRun[], kind: ChangeKind, token: CorrectionToken) => {
  const last = runs[runs.length - 1]
  if (last && last.kind === kind) last.tokens.push(token)
  else runs.push({ kind, tokens: [token] })
}

/**
 * Reads better as "was X, now Y" than the other way round, and the LCS walk
 * can emit either order depending on where the match landed.
 */
const removedBeforeAdded = (runs: CorrectionRun[]): CorrectionRun[] => {
  const ordered = [...runs]
  for (let i = 0; i < ordered.length - 1; i++) {
    if (ordered[i].kind === 'added' && ordered[i + 1].kind === 'removed') {
      const added = ordered[i]
      ordered[i] = ordered[i + 1]
      ordered[i + 1] = added
    }
  }
  return ordered
}

/** One swap (struck words immediately followed by their replacement) is one change. */
const countChanges = (runs: CorrectionRun[]): number => {
  let count = 0
  let inChange = false
  runs.forEach((run) => {
    if (run.kind === 'same') {
      inChange = false
      return
    }
    if (!inChange) count++
    inChange = true
  })
  return count
}

/**
 * The marked-up correction: what the teacher kept, struck and added, plus any
 * remarks they left at the end.
 *
 * With no corrected text at all (the teacher answered by voice, or the row
 * predates written corrections) every token comes back as `same`, so the
 * caller can render the learner's own answer unchanged instead of a diff
 * against nothing.
 */
export const buildCorrectionMarkup = (written: unknown, corrected: unknown): CorrectionMarkup => {
  const before = asTokens(written).filter((t) => !isBlank(t))
  const { body, notes } = splitTeacherNotes(corrected)
  const after = body.filter((t) => !isBlank(t))

  if (!after.length) {
    return { runs: before.length ? [{ kind: 'same', tokens: before }] : [], notes, changeCount: 0 }
  }
  if (!before.length) {
    return { runs: [{ kind: 'same', tokens: after }], notes, changeCount: 0 }
  }

  const [keptBefore, keptAfter] = commonSubsequence(before, after)
  const runs: CorrectionRun[] = []
  let i = 0
  let j = 0

  while (i < before.length || j < after.length) {
    if (i < before.length && !keptBefore[i]) {
      pushRun(runs, 'removed', before[i])
      i++
    } else if (j < after.length && !keptAfter[j]) {
      pushRun(runs, 'added', after[j])
      j++
    } else if (i < before.length && j < after.length) {
      pushRun(runs, 'same', after[j])
      i++
      j++
    } else {
      // One side ran out while the other still has matched tokens: impossible
      // for a well-formed LCS, but bail rather than spin.
      break
    }
  }

  const ordered = removedBeforeAdded(runs)
  return { runs: ordered, notes, changeCount: countChanges(ordered) }
}

/** Plain text of one side of the markup, for previews and screen readers. */
export const markupToText = (runs: CorrectionRun[], view: CorrectionView): string =>
  (runs ?? [])
    .filter((run) => runIsVisible(run.kind, view))
    .map((run) => run.tokens.map((t) => t.word).join(''))
    .join('')
